import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import { Tooltip, Typography } from "@mui/material";

import FormatCurrency from "../../../../../../../../global/FormatCurrency";
import HNSColors from "../../../../../../../../ui library/HNSColors";
import InNetworkIcon from "../../../../../../../../ui library/icons/InNetworkIcon";
import OutOfNetworkIcon from "../../../../../../../../ui library/icons/OutOfNetworkIcon";
import { useFieldOptionsMedicalDocumentsStatus } from "../../hooks/useFieldOptionsMedicalDocumentsStatus";
import { useProCliStore } from "../../hooks/useProCliStore";
import { STRINGS } from "../utils/strings";

export default function ProviderListSummaryv2({ accordionItem }) {
  const { providersData } = useProCliStore();

  const proCliData = providersData.get(accordionItem.index)?.proCliData;

  const createSubTitle = (providerTypes = []) => {
    return providerTypes && providerTypes.length > 0
      ? `(${providerTypes.map((providerType) => providerType.name).join(", ")})`
      : "";
  };

  const getProcliIcon = (provider) => {
    if (provider && provider.inNetwork) {
      if (provider.receivingClients) {
        return <InNetworkIcon strokeColor={HNSColors.success[500]} />;
      } else {
        return <OutOfNetworkIcon strokeColor={HNSColors.warning[500]} />;
      }
    }
    return <OutOfNetworkIcon strokeColor={HNSColors.failure[500]} />;
  };

  const icon = getProcliIcon(proCliData?.provider);
  const title = proCliData?.provider?.name;
  const subTitle = createSubTitle(proCliData?.provider?.providerTypes);
  const inNetwork =
    proCliData?.provider.inNetwork !== null
      ? proCliData?.provider.inNetwork
        ? STRINGS.IN_NETWORK
        : STRINGS.OUT_OF_NETWORK
      : "";
  const notes =
    proCliData?.provider.inNetwork && proCliData?.provider.receivingClients === false
      ? STRINGS.NOT_ACCEPTING_PATIENTS
      : "";
  const currentBalance = FormatCurrency(proCliData?.currentBalance);
  const treatmentStatus = proCliData?.finishedTreatment
    ? STRINGS.TREATMENT_STATUS.COMPLETE
    : STRINGS.TREATMENT_STATUS.TREATING;
  const currentCharges = FormatCurrency(proCliData?.runningTotal);
  const providerTransportationValue = proCliData?.providerTransportation;
  const showProviderTransportationIcon = Boolean(providerTransportationValue);
  const showHnsTransportationIcon = Boolean(proCliData?.hnsTransportation);

  const medicalDocumentsStatus = proCliData?.medicalDocumentsStatus || 0;
  const { isPending, error, data } = useFieldOptionsMedicalDocumentsStatus();
  const documentStatusData =
    data && data.length > 0
      ? (data.find((item) => item.id === medicalDocumentsStatus)?.name ?? STRINGS.NA)
      : STRINGS.NA;
  const documentStatus = isPending ? "..." : error ? error.message : documentStatusData;
  const transportBadgeSx = (backgroundColor) => ({
    alignItems: "center",
    backgroundColor,
    border: "none",
    borderRadius: "999px",
    display: "inline-flex",
    height: "24px",
    justifyContent: "center",
    width: "24px",
  });
  const renderTransportationIcons = () => {
    if (!showProviderTransportationIcon && !showHnsTransportationIcon) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        {showProviderTransportationIcon ? (
          <Tooltip title="Provider Transportation" arrow>
            <span style={transportBadgeSx(HNSColors.primary[900])}>
              <DirectionsCarFilledOutlinedIcon
                sx={{ fontSize: "0.9rem", color: HNSColors.primary[500] }}
              />
            </span>
          </Tooltip>
        ) : null}
        {showHnsTransportationIcon ? (
          <Tooltip title="HNS Transportation" arrow>
            <span style={transportBadgeSx(HNSColors.secondary[900])}>
              <DirectionsCarFilledOutlinedIcon
                sx={{ fontSize: "0.9rem", color: HNSColors.secondary[500] }}
              />
            </span>
          </Tooltip>
        ) : null}
      </div>
    );
  };

  return (
    <div className="flex w-full flex-row items-center justify-between pr-2 normal-case">
      <div className="flex w-3/5 flex-row items-center px-4">
        <div>{icon}</div>
        <div className="flex flex-col px-4">
          {title && (
            <Typography className="text-md pb-1 text-left font-bold normal-case text-black">
              <span>{title}</span>
              {inNetwork ? (
                <span className="pb-1 pl-1 text-left text-sm font-normal normal-case text-black">{` - ${inNetwork}`}</span>
              ) : null}
            </Typography>
          )}

          <div className="flex flex-col gap-1">
            {subTitle ? (
              <Typography className="left-align truncate text-left text-xs normal-case text-gray-500">
                <span>{subTitle}</span>
              </Typography>
            ) : null}

            {notes ? (
              <Typography className="left-align truncate text-left text-xs normal-case text-gray-500">
                <span className="font-bold text-black">
                  {"Note: "}&nbsp;
                  <span className="font-normal">{notes}</span>
                </span>
              </Typography>
            ) : null}

            {notes ? renderTransportationIcons() : null}
            {!notes ? renderTransportationIcons() : null}
          </div>
        </div>
      </div>
      <div className="flex w-2/5 flex-col justify-end">
        <div className="flex w-full flex-row justify-end gap-2">
          <div className="w-1/2">
            <Typography className="pb-1 text-left text-sm font-bold normal-case text-black">
              {`Verbal Balance: `}&nbsp;
              <span className="font-normal" style={{ whiteSpace: "pre" }}>
                {currentBalance !== null ? currentBalance : STRINGS.NA.padEnd(10, " ")}
              </span>
            </Typography>
          </div>
          {treatmentStatus && (
            <div className="w-1/2">
              <Typography className="pb-1 text-left text-sm font-bold normal-case text-black">
                {`Status: `}
                {<span className="font-normal">{treatmentStatus.padEnd(15, " ")}</span>}
              </Typography>
            </div>
          )}
        </div>
        <div className="flex w-full flex-row justify-end gap-2">
          <div className="w-1/2">
            <Typography className="pb-1 text-left text-sm font-bold normal-case text-black">
              {`Received Bill Charges: `}&nbsp;
              <span className="font-normal" style={{ whiteSpace: "pre" }}>
                {currentCharges !== null ? currentCharges : STRINGS.NA.padEnd(10, " ")}
              </span>
            </Typography>
          </div>
          {documentStatus && (
            <div className="w-1/2">
              <Typography className="pb-1 text-left text-sm font-bold normal-case text-black">
                {`Docs: `}
                {<span className="font-normal">{documentStatus.padEnd(15, " ")}</span>}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
